import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { pillarArticles } from '@/mocks/pillarArticles';
import { MAIN_SITE_URL } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';
import { buildArticleSchema, absoluteUrl } from '@/seo/schema';

const PUBLISH_DATE = '2026-01-15';
const UPDATE_DATE = '2026-08-21';

interface GuideItem {
  title: string;
  desc: string;
}

export default function GuidePage() {
  const { t } = useTranslation();
  const { slug } = useParams();

  const foundIndex = pillarArticles.findIndex((a) => a.slug === slug);
  const index = foundIndex >= 0 ? foundIndex : 0;
  const article = pillarArticles[index];

  const items = t('guides.items', { returnObjects: true }) as GuideItem[];
  const current = items[index] || items[0];
  const points = t('guides.detail.points', { returnObjects: true }) as string[];

  const articleSchema = buildArticleSchema({
    headline: current.title,
    description: current.desc,
    image: article.image,
    url: absoluteUrl(`/guide/${slug}`),
    datePublished: PUBLISH_DATE,
    dateModified: UPDATE_DATE,
  });

  const related = pillarArticles
    .map((a, i) => ({ ...a, index: i }))
    .filter((a) => a.index !== index)
    .slice(0, 6);

  return (
    <>
      <title>{current.title}</title>
      <meta name="description" content={current.desc} />
      <JsonLd data={articleSchema} />
      <div className="min-h-screen bg-background-50">
        <Header />
        <main>
          <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600 transition-colors">
                {t('common.brand')}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/#guides" className="hover:text-primary-600 transition-colors">
                {t('guides.sectionTitle')}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">{current.title}</span>
            </nav>

            <h1 className="mt-6 text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
              {current.title}
            </h1>

            <div className="mt-6 rounded-lg overflow-hidden border border-background-200">
              <img
                src={article.image}
                alt={current.title}
                title={current.title}
                className="w-full h-56 md:h-72 object-cover object-top"
              />
            </div>

            <p className="mt-8 text-base md:text-lg text-foreground-800 leading-relaxed">
              {current.desc}
            </p>

            <section className="mt-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                {t('guides.detail.pointsTitle')}
              </h2>
              <ul className="mt-5 flex flex-col gap-3">
                {points.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                    <i className="ri-checkbox-circle-line text-lg text-primary-500 mt-0.5"></i>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-12">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950">
                {t('guides.detail.relatedTitle')}
              </h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {related.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/guide/${a.slug}`}
                    className="flex items-center gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 hover:bg-primary-50/50 transition-colors"
                  >
                    <span className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${a.icon} text-base`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 leading-snug">
                      {items[a.index]?.title}
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            <div className="mt-10 p-6 rounded-lg bg-primary-50 border border-primary-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-foreground-700">{t('home.hero.trust')}</p>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('guides.detail.visitMain')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <div className="mt-6 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                {t('guides.detail.backHome')}
              </Link>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}